<template>
    <component
        v-if="iconComponent"
        :is="iconComponent"
        :class="twMerge('block flex-shrink-0', attrs.class)"
        :style="iconStyles"
        v-bind="attrs"
    />
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { twMerge } from 'tailwind-merge'
import { icons } from './helpers/iconMap'
import type { IconName } from './types'

type Props = {
    name: IconName
    size?: number
    variant?: 'outline' | 'solid'
}

const attrs = useAttrs() as Record<string, string>

const props = withDefaults(defineProps<Props>(), {
    size: 24,
})

const computedVariant = computed(() => {
    if (props.variant) {
        return props.variant
    }
    // If size is smaller than 16 (inclusive), use micro icons
    if (props.size <= 16) {
        return 'micro'
    }

    // If size is between 16 and 20 (inclusive), use mini icons
    if (props.size <= 20) {
        return 'mini'
    }

    // Otherwise use the specified variant (solid or outline)
    return 'outline'
})

const iconStyles = computed(() => ({ width: `${props.size}px`, height: `${props.size}px` }))

const iconComponent = computed(() => {
    const exists = props.name in icons

    if (!exists) {
        console.error(`[Icon] Icon "${props.name}" not found`)
        return null
    }

    const variantExists = computedVariant.value in icons[props.name]

    if (!variantExists) {
        console.error(`[Icon] Icon variant "${computedVariant.value}" not available for "${props.name}"`)
        return null
    }

    return icons[props.name][computedVariant.value]
})

</script>
